import type { Query } from '@tanstack/react-query';

/** True when the document tab is visible to the user. */
export function isPageVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState === 'visible';
}

/** Returns the interval when visible, otherwise pauses polling. */
export function pollIntervalMs(ms: number): number | false {
  return isPageVisible() ? ms : false;
}

type RefetchWhenActiveOptions<TData> = {
  intervalMs: number;
  /** When false, polling stops even if the tab is visible. */
  shouldPoll?: (data: TData | undefined) => boolean;
};

/** Slower cadence used after a failed poll, so a blip does not hammer the API. */
const ERROR_BACKOFF_MS = 15_000;

/**
 * Standard refetchInterval callback: pause on hidden tab or when shouldPoll
 * returns false. A query that never loaded stops on error (e.g. a 404); one
 * that had data keeps polling, slower, so a single failed request (API
 * restart, network blip) cannot freeze a progress screen for good.
 */
export function refetchWhenActive<TData>(
  query: Query<TData>,
  { intervalMs, shouldPoll }: RefetchWhenActiveOptions<TData>,
): number | false {
  const failed = query.state.status === 'error';
  if (failed && query.state.data === undefined) return false;
  if (!isPageVisible()) return false;
  if (shouldPoll && !shouldPoll(query.state.data)) return false;
  return failed ? Math.max(intervalMs, ERROR_BACKOFF_MS) : intervalMs;
}

export function understandingStillRunning(data: { parent_status?: string; workers?: Array<{ status: string }> } | undefined): boolean {
  if (!data) return true;
  const workers = data.workers ?? [];
  const active = (s: string) => {
    const v = (s || '').toLowerCase();
    return (
      v === 'running' ||
      v === 'pending' ||
      v === 'queued' ||
      v === 'in_progress' ||
      v === 'processing' ||
      v === 'active'
    );
  };
  if (workers.some((w) => active(w.status))) return true;
  const parent = (data.parent_status || '').toLowerCase();
  if (parent === 'running' || parent === 'pending') return true;
  // Parent can flip to completed while seats are still open (re-ingest / reconcile).
  if (workers.length > 0 && workers.some((w) => !['completed', 'failed', 'skipped'].includes((w.status || '').toLowerCase()))) {
    return true;
  }
  if (parent === 'completed' || parent === 'failed') return false;
  if (workers.length === 0) return parent === 'running' || parent === 'pending';
  return false;
}

export function tasksStillRunning(tasks: Array<{ status?: string }> | undefined): boolean {
  if (!tasks?.length) return false;
  return tasks.some((t) => {
    const s = (t.status ?? '').toLowerCase();
    return s === 'running' || s === 'pending' || s === 'queued' || s === 'in_progress';
  });
}

export function workflowNeedsFastPoll(
  data:
    | {
        active_workers?: Array<{ status: string }>;
        phases?: Array<{ status: string }>;
      }
    | undefined,
): boolean {
  if (!data) return true;
  if (data.active_workers?.some((w) => w.status === 'running' || w.status === 'pending')) return true;
  return data.phases?.some((p) => p.status === 'running' || p.status === 'active') ?? false;
}
